import React, { useState } from "react";
import wlogo from "./pics/w_logo.png"

const NavButton = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${active ? "bg-gray-900 text-white shadow" : "hover:bg-gray-100"
      }`}
  >
    <span className="text-xl" aria-hidden>{icon}</span>
    <span className="font-medium">{label}</span>
  </button>
);

const StatCard = ({ title, value, caption }) => (
  <div className="rounded-2xl border p-5 shadow-sm bg-white">
    <div className="text-sm text-gray-500">{title}</div>
    <div className="text-3xl font-semibold mt-1">{value}</div>
    {caption && <div className="text-xs text-gray-400 mt-2">{caption}</div>}
  </div>
);

const Table = ({ columns, rows }) => (
  <div className="overflow-x-auto rounded-2xl border bg-white">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          {columns.map((c, i) => (
            <th key={i} className="px-4 py-3 text-left font-semibold text-gray-700">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t">
            {r.map((cell, j) => (
              <td key={j} className="px-4 py-3">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function LoginScreen({ onDemoLogin }) {
  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-b from-white to-gray-50">
      <div className="w-full max-w-md rounded-3xl border shadow-xl p-8 bg-white">
        <div className="flex items-center gap-3 mb-6">
          {/* Logo */}
          <img
            src={wlogo}
            alt="W"
            className="h-10 w-10 object-contain rounded-lg"
          />
          <div>
            <div className="text-xl font-semibold">Wokmat</div>
            <div className="text-xs text-gray-500">AI Evaluation Platform</div>
          </div>
        </div>

        <h1 className="text-2xl font-semibold mb-2">Welcome back</h1>
        <p className="text-sm text-gray-500 mb-6">
          Sign in to manage exams and AI grading.
        </p>

        <div className="grid gap-3">
          <button
            onClick={onDemoLogin}
            className="w-full border rounded-xl py-3 hover:bg-gray-50"
          >
            Continue with Google
          </button>
          <button
            onClick={onDemoLogin}
            className="w-full border rounded-xl py-3 hover:bg-gray-50"
          >
            Continue with Microsoft
          </button>
          <button
            onClick={onDemoLogin}
            className="w-full border rounded-xl py-3 hover:bg-gray-50"
          >
            Continue with GitHub
          </button>
        </div>
      </div>
    </div>
  );
}


function Topbar({ onLogout }) {
  return (
    <div className="h-16 border-b bg-white flex items-center justify-between px-6">
      <div className="flex items-center gap-3">
        <img
          src={wlogo}
          alt="W"
          className="h-10 w-10 object-contain rounded-lg"
        />
        <div className="font-semibold">Wokmat</div>
        <div className="text-xs text-gray-400 ml-3">AI Evaluation Platform</div>
      </div>
      <div className="flex items-center gap-4">
        <input
          className="hidden md:block border rounded-xl px-3 py-2 w-64"
          placeholder="Search exams, students..."
        />
        <div className="h-9 w-9 grid place-items-center rounded-xl border">🔔</div>
        <div className="h-9 w-9 grid place-items-center rounded-xl border">👤</div>
        <button
          onClick={onLogout}
          className="text-xs border rounded-lg px-2 py-1"
        >
          Log out
        </button>
      </div>
    </div>
  );
}

function Shell({ current, setCurrent, onLogout }) {
  const items = [
    { id: "dashboard", label: "Dashboard", icon: "" },
    { id: "upload", label: "Exam Upload", icon: "" },
    { id: "grading", label: "Batch Grading", icon: "" },
    { id: "qa", label: "Q/A Panel", icon: "" },
    { id: "admin", label: "Admin / Teacher", icon: "" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar onLogout={onLogout} />
      <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="border-r bg-white p-4 md:min-h-[calc(100vh-4rem)]">
          <div className="text-xs text-gray-500 mb-3">NAVIGATION</div>
          <div className="grid gap-2">
            {items.map((it) => (
              <NavButton
                key={it.id}
                icon={it.icon}
                label={it.label}
                active={current === it.id}
                onClick={() => setCurrent(it.id)}
              />
            ))}
          </div>
        </aside>
        <main className="p-6">
          {current === "dashboard" && <Dashboard />}
          {current === "upload" && <ExamUpload />}
          {current === "grading" && <GradingResults />}
          {current === "qa" && <QAPanel />}
          {current === "admin" && <AdminPanel />}
        </main>
      </div>
    </div>
  );
}
function Dashboard() {
  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Active Exams" value="6" caption="Ongoing across 3 courses" />
        <StatCard title="Submissions Today" value="182" caption="+12% vs yesterday" />
        <StatCard title="Avg. AI Grade Time" value="42s" caption="per submission" />
        <StatCard title="Flagged for Review" value="9" caption="potential anomalies" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border p-5 bg-white">
          <div className="font-semibold mb-2">Recent Exams</div>
          <Table
            columns={["Exam", "Course", "Status", "Submissions"]}
            rows={[
              ["Midterm: ML101", "Intro to ML", "Open", "74"],
              ["Quiz 3: Algo", "Algorithms", "Closed", "52"],
              ["Final: NLP", "NLP", "Grading", "108"],
            ]}
          />
        </div>
        <div className="rounded-2xl border p-5 bg-white">
          <div className="font-semibold mb-2">Grading Throughput</div>
          <div className="h-48 rounded-xl border grid place-items-center text-sm text-gray-400">
            Placeholder for chart (throughput over time)
          </div>
        </div>
      </div>
    </div>
  );
}

function ExamUpload() {
  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold">Exam Upload</h1>
      <div className="rounded-2xl border p-6 bg-white grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600">Exam Title</label>
            <input className="w-full border rounded-xl px-3 py-2" placeholder="e.g., Midterm: Data Structures" />
          </div>
          <div>
            <label className="text-sm text-gray-600">Course</label>
            <input className="w-full border rounded-xl px-3 py-2" placeholder="e.g., CS201" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-gray-600">Exam Type</label>
            <select className="w-full border rounded-xl px-3 py-2">
              <option>Descriptive (Q/A)</option>
              <option>MCQ</option>
              <option>Mixed</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Time Window</label>
            <input className="w-full border rounded-xl px-3 py-2" placeholder="2025-11-15 10:00 → 12:00" />
          </div>
          <div>
            <label className="text-sm text-gray-600">Max Marks</label>
            <input className="w-full border rounded-xl px-3 py-2" placeholder="100" />
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-600">Upload Exam File (PDF/CSV)</label>
          <div className="border-2 border-dashed rounded-2xl p-6 text-center text-sm text-gray-500">
            Drag & drop files here or <span className="underline">browse</span>
          </div>
        </div>
        <div>
          <label className="text-sm text-gray-600">Rubric / Model Answers (Optional)</label>
          <textarea className="w-full border rounded-xl px-3 py-2 min-h-[120px]" placeholder="Paste rubric or upload a file" />
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 rounded-xl bg-black text-white">Validate</button>
          <button className="px-4 py-2 rounded-xl border">Save as Draft</button>
          <button className="px-4 py-2 rounded-xl border">Publish</button>
        </div>
      </div>
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const map = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    blue: "bg-blue-100 text-blue-800",
  };
  return <span className={`px-2 py-1 rounded-lg text-xs ${map[tone]}`}>{children}</span>;
}

function Actions() {
  return (
    <div className="flex items-center gap-2 text-sm">
      <button className="px-2 py-1 rounded-lg border">View</button>
      <button className="px-2 py-1 rounded-lg border">Regrade</button>
    </div>
  );
}

function GradingResults() {
  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold">AI Grading Results</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Avg Score" value="76/100" caption="All submissions" />
        <StatCard title="Auto-Graded" value="173" caption="of 182 submissions" />
        <StatCard title="Manual Reviews" value="9" caption="flagged by AI" />
        <StatCard title="Median Turnaround" value="38s" caption="submission ➜ grade" />
      </div>
      <div className="rounded-2xl border p-5 bg-white grid gap-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Submissions</div>
          <div className="flex items-center gap-2 text-sm">
            <input className="border rounded-xl px-3 py-2" placeholder="Search student or ID" />
            <select className="border rounded-xl px-3 py-2">
              <option>All statuses</option>
              <option>Flagged</option>
              <option>Regraded</option>
            </select>
          </div>
        </div>
        <Table
          columns={["Student", "ID", "Score", "Status", "Actions"]}
          rows={[
            ["Aarav Gupta", "S10293", <span className="font-medium">84</span>, <Badge tone="green">Auto-graded</Badge>, <Actions />],
            ["Isha Sharma", "S10247", <span className="font-medium">71</span>, <Badge tone="yellow">Flagged</Badge>, <Actions />],
            ["Meera Nair", "S10311", <span className="font-medium">92</span>, <Badge tone="green">Auto-graded</Badge>, <Actions />],
          ]}
        />
      </div>
      <div className="rounded-2xl border p-5 bg-white grid gap-3">
        <div className="font-semibold">AI Explanation (Selected Submission)</div>
        <div className="text-sm text-gray-600">Rationale summary</div>
        <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
          <li>
            Semantic alignment of answer with rubric: <b>High</b>.
          </li>
          <li>
            Coverage of key points: <b>8/10</b>. Missing discussion on time complexity.
          </li>
          <li>
            Writing clarity and structure: <b>Good</b>.
          </li>
          <li>
            Detected paraphrase probability: <b>Low</b>.
          </li>
        </ul>
        <div className="flex gap-2">
          <button className="px-3 py-2 rounded-xl border">Regrade</button>
          <button className="px-3 py-2 rounded-xl border">Override</button>
          <button className="px-3 py-2 rounded-xl bg-black text-white">Export</button>
        </div>
      </div>
    </div>
  );
}

function QAPanel() {
  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold">Q/A Panel</h1>
      <div className="rounded-2xl border p-6 bg-white grid gap-4">
        <div className="grid md:grid-cols-[2fr_1fr] gap-6">
          <div className="grid gap-3">
            <label className="text-sm text-gray-600">Question</label>
            <textarea
              className="w-full border rounded-xl px-3 py-2 min-h-[120px]"
              placeholder="Type or paste a descriptive question"
            />
            <label className="text-sm text-gray-600">Model Answer (optional)</label>
            <textarea
              className="w-full border rounded-xl px-3 py-2 min-h-[80px]"
              placeholder="Provide reference points for grading"
            />
            <div className="flex gap-2">
              <button className="px-4 py-2 rounded-xl bg-black text-white">Submit</button>
              <button className="px-4 py-2 rounded-xl border">Clear</button>
            </div>
          </div>
          <div className="grid gap-3">
            <label className="text-sm text-gray-600">Recent Q/A</label>
            <div className="grid gap-3">
              <div className="rounded-xl border p-3">
                <div className="text-xs text-gray-500">#10293 • DS Midterm</div>
                <div className="font-medium">Explain quicksort and its average-case complexity.</div>
                <div className="text-sm text-gray-600 mt-1">Avg Score: 78/100</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-xs text-gray-500">#10247 • Algo Quiz</div>
                <div className="font-medium">
                  What is a stable sorting algorithm? Give examples.
                </div>
                <div className="text-sm text-gray-600 mt-1">Avg Score: 71/100</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPanel() {
  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold">Admin / Teacher Panel</h1>
      <div className="rounded-2xl border p-6 bg-white grid gap-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border p-4">
            <div className="font-semibold">Create Course</div>
            <div className="grid gap-2 mt-2">
              <input className="border rounded-xl px-3 py-2" placeholder="Course Code (e.g., CS201)" />
              <input className="border rounded-xl px-3 py-2" placeholder="Course Title" />
              <button className="px-3 py-2 rounded-xl bg-black text-white">Add Course</button>
            </div>
          </div>
          <div className="rounded-2xl border p-4">
            <div className="font-semibold">Invite Teacher</div>
            <div className="grid gap-2 mt-2">
              <input className="border rounded-xl px-3 py-2" placeholder="Name" />
              <input className="border rounded-xl px-3 py-2" placeholder="Email" />
              <button className="px-3 py-2 rounded-xl border">Send Invite</button>
            </div>
          </div>
          <div className="rounded-2xl border p-4">
            <div className="font-semibold">Policies</div>
            <div className="grid gap-2 mt-2 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" /> Enable AI auto-grade
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" /> Require manual review if confidence &lt; 0.7
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" /> Allow student regrade request
              </label>
            </div>
          </div>
        </div>
        <div className="grid gap-3">
          <div className="font-semibold">People & Roles</div>
          <Table
            columns={["Name", "Role", "Course", "Status"]}
            rows={[
              ["Prof. D. Juneja", "Admin", "CS201", <Badge tone="blue">Active</Badge>],
              ["R. Kapoor", "Teacher", "ML101", <Badge tone="blue">Active</Badge>],
              ["A. Singh", "TA", "Algo", <Badge tone="gray">Invited</Badge>],
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [current, setCurrent] = useState("dashboard");

  if (!loggedIn) {
    return <LoginScreen onDemoLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div>
      <Shell
        current={current}
        setCurrent={setCurrent}
        onLogout={() => setLoggedIn(false)}
      />
    </div>
  );
}
export function runSmokeTests() {
  return [
    { name: "App is a function", pass: typeof App === "function" },
    { name: "Nav ids present", pass: ["dashboard", "upload", "grading", "qa", "admin"].length === 5 },
  ];
}
